// Eskom PARCC — compliance evaluation working-paper workbook builder.
//
// The monolith's `downloadWorkingPaperComplianceSheet` rebuilt from CLEAN typed
// data: every transaction is grouped by its procurement route (one sheet per
// route), and each sheet's rows reproduce the working-paper columns
// (`buildWorkingPaperRows`) — one row per CES checklist row, joined by
// `referenceNo`, or a single base row when a transaction has no CES rows.
//
// The row/column logic is PURE and tested as data: `buildComplianceSheets`
// returns `{ sheetName, rows: string[][] }[]` (each `rows[0]` is the header). A
// thin `workbookFromSheets` (see ./workbook) turns that into an XLSX WorkBook —
// no XLSX is touched here, so the data shape unit-tests without the binary.
import type { Transaction } from '../../data/types';
import type { CesRowRead } from '../../lib/db';
import { neutraliseCell } from './neutraliseCell';

/** Map from a transaction `referenceNo` to its (seq-sorted) CES rows. */
export type CesRowsByRef = ReadonlyMap<string, CesRowRead[]>;

/** The working-paper columns, in the monolith's `allWorkingPaperHeaders` order. */
export const COMPLIANCE_SHEET_HEADERS = [
  'Captured By Email',
  'Captured Date',
  'Mechanism / Evaluation Sheet / Procurement Route',
  'Dataset',
  'Reference No',
  'Division',
  'Supplier Name / Vendor Number',
  'CSD Number',
  'Value of Contract / PO',
  'Year',
  'Period',
  'Gaps Status',
  'Compliance Level',
  'Initial Findings',
  'Updated Findings',
  'Additional Comments / Findings',
  'Link',
  'Checklist Row No',
  'Checklist Stage',
  'Sub-Criteria',
  'Checklist Compliance Level',
  'Finding / Evidence',
] as const;

/** A built sheet: its (Excel-safe) name and its rows (row 0 is the header). */
export interface ComplianceSheet {
  sheetName: string;
  rows: string[][];
}

/**
 * A transaction's procurement route — the sheet it groups into. The monolith
 * read `raw['Evaluation Sheet'] || mechanism || raw['Mechanism']`; the typed
 * model surfaces only `mechanism`, so we fall back to 'Unspecified Route'.
 */
export function routeOf(tx: Transaction): string {
  const mech = (tx.mechanism ?? '').trim();
  return mech || 'Unspecified Route';
}

/**
 * Make an Excel-safe, unique worksheet name from a route name, applying ALL of
 * Excel's sheet-name rules so the workbook never opens with "we found a problem
 * with some content … repaired worksheet properties":
 *  - strip the forbidden characters (`\ / ? * [ ] :`) and collapse whitespace;
 *  - a name may not start or end with an apostrophe;
 *  - `History` is reserved (case-insensitive) and may not be used;
 *  - cap at Excel's 31-character limit; fall back to a default when empty;
 *  - names must be unique CASE-INSENSITIVELY (Excel treats "RFQ" and "rfq" as the
 *    same sheet — two such `<sheet>` entries are what corrupts workbook.xml), so
 *    we disambiguate collisions with a numeric suffix.
 * The `used` set is mutated to record the (lower-cased) name returned.
 */
export function cleanSheetName(name: string, used: Set<string>): string {
  let base = String(name ?? '')
    .replace(/[\\/?*[\]:]/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/^'+|'+$/g, '') // leading/trailing apostrophes are forbidden
    .trim()
    .slice(0, 31)
    .trim();
  if (!base || /^history$/i.test(base)) base = 'Route';
  let out = base;
  let i = 2;
  while (used.has(out.toLowerCase())) {
    const suffix = ' ' + i++;
    out = (base.slice(0, 31 - suffix.length).trim() || 'Route') + suffix;
  }
  used.add(out.toLowerCase());
  return out;
}

/** The compliance level shown/exported: the strict-rule override, else the level. */
function complianceLevel(tx: Transaction): string {
  return (tx.complianceLevelOverride || tx.complianceLevel || '') as string;
}

/** Display value for the contract/PO value (the formatted string, else the number). */
function valueDisplay(tx: Transaction): string {
  if (tx.valueDisplay) return tx.valueDisplay;
  return tx.value ? String(tx.value) : '';
}

/** The transaction's base columns (everything before the per-checklist columns). */
function baseCells(tx: Transaction): string[] {
  return [
    tx.capturedByEmail ?? '',
    tx.capturedDate ?? '',
    routeOf(tx),
    tx.dataset ?? '',
    tx.referenceNo ?? '',
    tx.division ?? '',
    tx.supplier ?? '',
    tx.csdNumber ?? '',
    valueDisplay(tx),
    tx.year ?? '',
    tx.period ?? '',
    (tx.gapsStatus ?? '') as string,
    complianceLevel(tx),
    tx.initialFindings ?? '',
    tx.updatedFindings ?? '',
    tx.workingPaperNotes ?? '',
    tx.link ?? '',
  ];
}

/** Blank checklist columns (for a transaction with no CES rows). */
const EMPTY_CHECKLIST: string[] = ['', '', '', '', ''];

/** Checklist columns for one CES row. */
function checklistCells(row: CesRowRead): string[] {
  return [
    String(row.seq ?? ''),
    row.stage ?? '',
    row.criterion ?? '',
    row.status ?? '',
    row.finding ?? '',
  ];
}

/**
 * Build the working-paper rows for a single transaction: one row per CES row
 * (base columns + that checklist row), or a single base row with blank checklist
 * columns when the transaction has no CES rows. Mirrors `buildWorkingPaperRows`.
 */
export function buildTransactionRows(tx: Transaction, cesRowsByRef: CesRowsByRef): string[][] {
  const base = baseCells(tx);
  const ces = cesRowsByRef.get(tx.referenceNo) ?? [];
  const rows =
    ces.length === 0
      ? [[...base, ...EMPTY_CHECKLIST]]
      : ces.map((row) => [...base, ...checklistCells(row)]);
  // Defence-in-depth: neutralise any free-text cell a spreadsheet could read as a formula.
  return rows.map((row) => row.map(neutraliseCell));
}

/**
 * Group transactions by procurement route and build one {@link ComplianceSheet}
 * per route, each with a leading header row followed by the working-paper rows
 * for that route's transactions. Sheets are ordered by route name (stable
 * `localeCompare`); sheet names are made Excel-safe and unique. Returns `[]` when
 * there are no transactions. Pure — no XLSX, no DOM.
 */
export function buildComplianceSheets(
  transactions: readonly Transaction[],
  cesRowsByRef: CesRowsByRef,
): ComplianceSheet[] {
  if (transactions.length === 0) return [];

  // Group transaction rows by route, preserving input order within a route.
  const byRoute = new Map<string, string[][]>();
  for (const tx of transactions) {
    const route = routeOf(tx);
    const rows = buildTransactionRows(tx, cesRowsByRef);
    const bucket = byRoute.get(route);
    if (bucket) bucket.push(...rows);
    else byRoute.set(route, rows);
  }
  if (byRoute.size === 0) return [];

  const header = [...COMPLIANCE_SHEET_HEADERS];
  const used = new Set<string>();
  return [...byRoute.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([route, rows]) => ({
      sheetName: cleanSheetName(route, used),
      rows: [header, ...rows],
    }));
}
